import { selectPortfolioDataLoadingStates } from "@shared/slices/portfolio.slice.ts";
import React, { CSSProperties, useCallback, useContext, useMemo } from "react";
import usePortfolioData from "features/portfolio/common/usePortfolioData.ts";
import {
  ConsolidatedCoinData,
  ConsolidatedCoinDataWithKey,
  GetPortfolioDataResponse,
  LanguageNameSpaces,
  PrecisionType,
  QueryStatus,
} from "@shared/types";
import TwoChartsTableLayout from "common/Layouts/TwoChartsTableLayout.tsx";
import CoinsPricesChart from "features/portfolio/common/CoinsPricesChart.tsx";
import ChangeLeverageModal from "features/portfolio/Positions/ChangeLeverageModal.tsx";
import {
  LAYOUT_DISTANCE_FROM_FOOTER_TWO_ROWS,
  LAYOUT_DISTANCE_FROM_HEADER,
} from "features/portfolio/common/constants.ts";
import { PrecisionConfig } from "types";
import CoinsPieChart from "features/portfolio/common/CoinsPieChart.tsx";
import { PositionsContext } from "features/portfolio/Positions/Positions.context.ts";
import { PortfolioContext } from "features/portfolio/Portfolio.context.ts";
import BuyButton from "common/Buttons/BuyButton.tsx";
import TwoChartsFlatListLayout from "common/Layouts/TwoChartsFlatListLayout.tsx";
import { useAssetsData } from "features/portfolio/common/hooks.tsx";
import EmptyView from "common/Empty/EmptyView.tsx";
import { EmptyStatesType } from "common/Icons/types.ts";
import { portfolioFilters } from "@shared/slices/filters.slice.ts";
import PositionsTable from "features/portfolio/Positions/PositionsTable.tsx";
import PositionCard from "features/portfolio/Positions/PositionCard.tsx";
import useMedia from "utils/hooks/useMedia.ts";
import TwoChartsTopContainer from "common/Layouts/TwoChartsTopContainer.tsx";
import FlatList from "common/Lists/FlatList.tsx";
import { BOTTOM_SPACER_HEIGHT } from "common/Home/General/constants.tsx";
import SortSelect from "common/Select/SortSelect.tsx";
import { PositionsSort } from "@shared/features/portfolio/types.ts";
import useSearchInput from "utils/hooks/useSearchInput.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { useSubUserPermissions } from "@shared/utils/hooks/misc.ts";
import { useTheme } from "styled-components";
import TextWithPrecisionAndIcon from "common/Text/TextWithPrecisionAndIcon.tsx";
import AllocationCard from "features/portfolio/NewLook/AllocationCard.tsx";
import NewLookPortfolioLayout from "features/portfolio/NewLook/NewLookPortfolioLayout.tsx";
import {
  NewLookCardsGrid,
  NewLookDrawerStack,
  NewLookMobileTop,
} from "features/portfolio/NewLook/styles.ts";
import { SectionHeader } from "common/NewLook";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay.tsx";
import PhonePositions from "features/portfolio/NewLook/PhonePositions.tsx";
import NewLookPositionsPnl from "features/portfolio/NewLook/NewLookPositionsPnl.tsx";

interface PositionsProps {
  dataSelector?: (state: any) => ConsolidatedCoinData[];
  loadingSelector?: (state: any) => { loading: boolean; loaded: boolean };
  precisionSelector?: (state: any) => PrecisionConfig | undefined;
  getPortfolioDataQuery?: QueryStatus<GetPortfolioDataResponse>;
}

const Positions = ({
  dataSelector: propsDataSelector,
  loadingSelector: propsLoadingSelector,
  precisionSelector: propsPrecisionSelector,
  getPortfolioDataQuery,
}: PositionsProps) => {
  const { t, i18n } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const {
    changeLeverageCoinData,
    setChangeLeverageCoinData,
    sortBy,
    sortDirection,
    setSortBy,
    setSortDirection,
  } = useContext(PositionsContext)!;
  const { isCardsView, searchValue, setBuyModalOpen } =
    useContext(PortfolioContext) || {};
  const isSmallScreen = useMedia([`(max-width: 1000px)`], [true], false);
  const filtersOnGrid = useMedia([`(min-width: 1400px)`], [true], false);
  const [searchInput, drawerSearchValue] = useSearchInput({
    placeholder: tCommon("labels.search_placeholder"),
    style: { width: 200, borderRadius: 8 },
  });
  const { isReadOnly } = useSubUserPermissions();
  const theme = useTheme();

  const {
    isBottomNavigator,
    onRefresh,
    dataSelector,
    positionsPieChartData,
    margin,
    isDrawer,
    precisionSelector,
    loading,
    loaded,
    getPortfolioDataLoading,
  } = usePortfolioData({
    dataSelector: propsDataSelector,
    loadingSelector: propsLoadingSelector,
    precisionSelector: propsPrecisionSelector,
    getPortfolioDataQuery,
    isPositions: true,
  });

  const {
    rawPortfolioData,
    sortedCoinsData: sortedPositions,
    topSymbol,
  } = useAssetsData({
    dataSelector,
    searchFilter: searchValue || drawerSearchValue,
    isPositions: true,
  });

  const emptyState = useMemo(
    () => (
      <EmptyView
        iconKey={EmptyStatesType.Coins}
        title={t("positions.empty_title")}
        iconWidth={isDrawer ? 100 : 140}
        objectName="positions"
        isSearch={!!searchValue?.length}
        hasData={!!rawPortfolioData?.length}
        filtersSlice={portfolioFilters}
      />
    ),
    [isDrawer, searchValue, rawPortfolioData, i18n.language]
  );

  const pieChart = useMemo(
    () => (
      <CoinsPieChart
        total={margin}
        pieChartData={positionsPieChartData}
        precisionSelector={precisionSelector}
        loadingSelector={selectPortfolioDataLoadingStates}
        tooltipElementId="positions-chart-tooltip"
        chartParentElementId="positions-chart"
        title={t("charts.positions_allocation")}
        hideTitle={isDrawer}
        extraInfoStyle={PIE_CHART_EXTRA_INFO_STYLE}
      />
    ),
    [margin, positionsPieChartData, i18n.language]
  );

  /** On desktop pages the allocation is a half of the overview panel. */
  const inOverview = theme.isNewLook && !isBottomNavigator && !isDrawer;

  const allocationCard = useMemo(
    () =>
      theme.isNewLook ? (
        <AllocationCard
          title={inOverview ? undefined : t("charts.positions_allocation")}
          bare={inOverview}
          compact={isBottomNavigator}
          data={positionsPieChartData}
          total={
            <TextWithPrecisionAndIcon
              precisionSelector={precisionSelector}
              precisionType={PrecisionType.AMOUNT}
              text={margin}
              round={true}
            />
          }
          loading={
            loading && (!isBottomNavigator || !positionsPieChartData.length)
          }
          chartId={isDrawer ? "positions-chart-drawer" : "positions-chart"}
          emptyTitle={tCommon("empty.allocation_no_positions")}
          emptyDescription={tCommon(
            "empty.allocation_no_positions_description"
          )}
        />
      ) : undefined,
    [
      theme.isNewLook,
      t,
      tCommon,
      positionsPieChartData,
      precisionSelector,
      margin,
      loading,
      isBottomNavigator,
      isDrawer,
      inOverview,
    ]
  );

  const coinsPricesChart = useMemo(() => {
    return (
      <CoinsPricesChart
        key="pieChart"
        pieChartData={positionsPieChartData}
        defaultSymbol={topSymbol?.symbol}
        defaultExchange={topSymbol?.exchanges?.[0]}
        loading={loading}
        loaded={loaded}
      />
    );
  }, [positionsPieChartData, sortedPositions, loading, loaded, topSymbol]);

  const positionsTable = useMemo(() => {
    return (
      <PositionsTable
        data={sortedPositions}
        loading={loading}
        emptyState={emptyState}
      />
    );
  }, [sortedPositions, loading, emptyState]);

  const renderContent = ({ item }: { item: ConsolidatedCoinDataWithKey }) => {
    return <PositionCard coinData={item} />;
  };

  const extraActions = useMemo(
    () =>
      isDrawer && (
        <>
          {searchInput}
          <SortSelect
            setSortBy={setSortBy}
            sortBy={sortBy}
            sortDirection={sortDirection}
            setSortDirection={setSortDirection}
            sortValues={Object.values(PositionsSort)}
            rounded={true}
            labelMapper={(value) => t(value)}
          />
        </>
      ),
    [isDrawer, drawerSearchValue, sortBy, sortDirection, i18n.language]
  );

  const renderTopContainer = useCallback(() => {
    if (theme.isNewLook && isDrawer && !isBottomNavigator) {
      return (
        <NewLookDrawerStack>
          {/* An account without positions: let the empty state lead. */}
          {(loading || positionsPieChartData.length > 0) && allocationCard}
          <SectionHeader title={t("tabs.positions")} actions={extraActions} />
        </NewLookDrawerStack>
      );
    }
    return (
      <TwoChartsTopContainer
        largeChartUpperMiddle={coinsPricesChart}
        smallChartUpperLeft={
          !theme.isNewLook ? (
            pieChart
          ) : loading || positionsPieChartData.length > 0 ? (
            <NewLookMobileTop>{allocationCard}</NewLookMobileTop>
          ) : undefined
        }
        extraActions={extraActions}
      />
    );
  }, [
    coinsPricesChart,
    pieChart,
    extraActions,
    allocationCard,
    theme.isNewLook,
    loading,
    positionsPieChartData.length,
    isDrawer,
    isBottomNavigator,
    t,
  ]);

  const flatList = (
    <FlatList
      data={sortedPositions}
      renderContent={renderContent}
      renderTopContainer={renderTopContainer}
      emptyState={emptyState}
      minColumnWidth={isBottomNavigator ? undefined : 350}
      paddingBottom={BOTTOM_SPACER_HEIGHT}
      isFixedTopContainer={!isDrawer}
      loading={getPortfolioDataLoading}
      refreshing={loading}
      onRefresh={onRefresh}
      wrapperStyle={{ padding: isBottomNavigator ? 16 : "0 24px" }}
    />
  );

  const getNewLookContent = () => {
    const cards = sortedPositions.length ? (
      <LoadingSpinnerOverlay
        isLoading={getPortfolioDataLoading}
        backgroundColor={theme.colors.layoutBgColor}
      >
        <NewLookCardsGrid $minWidth={320}>
          {sortedPositions.map((item) => (
            <PositionCard key={item.key} coinData={item} />
          ))}
        </NewLookCardsGrid>
      </LoadingSpinnerOverlay>
    ) : undefined;
    return (
      <NewLookPortfolioLayout
        chart={
          <NewLookPositionsPnl dataSelector={dataSelector} loading={loading} />
        }
        allocation={allocationCard}
        allocationTitle={t("charts.positions_allocation")}
        showOverview={loading || positionsPieChartData.length > 0}
        title={t("tabs.positions")}
        content={isCardsView ? cards ?? emptyState : positionsTable}
        isCards={isCardsView && !!cards}
      />
    );
  };

  const getPageContent = () => {
    if (theme.isNewLook && !isBottomNavigator && !isDrawer) {
      return getNewLookContent();
    }
    if (theme.isNewLook && isBottomNavigator && !isDrawer) {
      return (
        <div style={{ position: "relative", height: "100%" }}>
          <PhonePositions
            dataSelector={dataSelector}
            searchValue={searchValue}
            loading={getPortfolioDataLoading}
            refreshing={loading}
            onRefresh={onRefresh}
            onBuyClick={
              !!setBuyModalOpen && !isReadOnly
                ? () => setBuyModalOpen(true)
                : undefined
            }
          />
          {!!setBuyModalOpen && !isReadOnly && (
            <BuyButton
              onClick={() => setBuyModalOpen(true)}
              style={BUY_BUTTON_STYLE}
            />
          )}
        </div>
      );
    }
    if (isCardsView || isBottomNavigator || isDrawer) {
      return (
        <>
          <TwoChartsFlatListLayout
            flatList={flatList}
            distanceFromHeader={
              isDrawer
                ? undefined
                : isSmallScreen
                ? LAYOUT_DISTANCE_FROM_FOOTER_TWO_ROWS
                : LAYOUT_DISTANCE_FROM_HEADER
            }
          />
          {isBottomNavigator &&
            !!setBuyModalOpen &&
            !isDrawer &&
            !isReadOnly && (
              <BuyButton
                onClick={() => setBuyModalOpen(true)}
                style={BUY_BUTTON_STYLE}
              />
            )}
        </>
      );
    }
    return (
      <TwoChartsTableLayout
        mobileCharts={[coinsPricesChart, positionsTable]}
        smallChartUpperLeft={pieChart}
        largeChartUpperMiddle={coinsPricesChart}
        mainContent={positionsTable}
        useMobileCards={false}
        distanceFromHeader={LAYOUT_DISTANCE_FROM_HEADER}
        filtersOnGrid={filtersOnGrid}
      />
    );
  };

  return (
    <>
      {getPageContent()}
      <ChangeLeverageModal
        coinData={changeLeverageCoinData}
        closeModal={() => setChangeLeverageCoinData?.(undefined)}
        visible={!!changeLeverageCoinData}
      />
    </>
  );
};

export default Positions;

const PIE_CHART_EXTRA_INFO_STYLE = { maxWidth: 400 };
const BUY_BUTTON_STYLE: CSSProperties = { position: "absolute", bottom: 16 };
